function isIndexedDbAvailable() {
  return typeof window !== "undefined" && !!window.indexedDB;
}

/**
 * IndexedDB wrapper for caching data in the browser.
 *
 * This class provides methods to connect to an IndexedDB database,
 * query items with optional filtering/sorting/pagination, and
 * perform read/write transactions on the object store.
 *
 * Examples:
 * (0) Create an instance of IndexedDB with a database name, version, and object store definitions:
 *     const deinitions = [
 *       ["myObjectStore", { name: "myObjectStore", options: { keyPath: "id", autoIncrement: true } }],
 *     ];
 *     const cache = new IndexedDB("myCacheDb", 1, definitions);
 *
 * (1) Get a paginated list of items with optional filter and sort:
 *     const result = await cache.query("myObjectStore", {
 *       filter: (item) => item.type === "active",
 *       sort: (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
 *       pageNo: 0,
 *       pageSize: 20,
 *     });
 *
 *  (2) Insert items into the cache:
 *      await cache.insert("myObjectStore", { id: 1, type: "active", timestamp: new Date() });
 *
 *  (3) Retrieve a single item by key:
 *      const item = await cache.read("myObjectStore", 1);
 *
 *  (4) Remove items by key:
 *     await cache.delete("myObjectStore", 1);
 *
 *  (6) Update a single item by key (same as insert, since it will overwrite existing item with same key):
 *      await cache.insert("myObjectStore", { id: 1, type: "inactive", timestamp: new Date() });
 *
 */
export default class IndexedDB {
  #name = null;

  #options = {};

  #dbName;

  #dbVersion;

  #dbPromise;

  #onUpgrade;

  /**
   * Creates an instance of IndexedDB for a specific object store.
   *
   * @todo Might need to refactor this, as the current implementation assumes a
   *       single object store (=dbName) per database (=storeName), which is not ideal.
   *       We might want to allow multiple stores in the same database,
   *       but that would require some changes to how we handle database versioning and upgrades.
   *
   * @param {string} dbName - The name of the IndexedDB database to connect to.
   * @param {number} version - The version of the database, used to trigger onUpgrade when changed.
   * @param {Array|function(): Array<{name: string, options: object}>} definitions - A function that returns an array of object store definitions to create in the database, with their respective options (e.g., keyPath, autoIncrement).
   */
  constructor(dbName, version, definitions = () => []) {
    this.#dbName = dbName;
    this.#dbVersion = version;

    this.#onUpgrade = (event) => {
      const db = event.target.result;

      const dd =
        typeof definitions === "function" ? definitions() : definitions;

      // list of all object-store definitions
      // to ensure exist in the database
      for (const { name, options, $dropOnUpgrade = true } of dd) {
        if ($dropOnUpgrade && db.objectStoreNames.contains(name)) {
          db.deleteObjectStore(name);
        }

        db.createObjectStore(name, options);
      }
    };
  }

  /**
   * Connects to the IndexedDB database and ensures the object store exists.
   * @returns {Promise<IDBDatabase|null>} A promise that resolves with the database instance or null if IndexedDB is not available.
   */
  async connect() {
    if (!isIndexedDbAvailable()) {
      return null;
    }
    if (this.#dbPromise) {
      return this.#dbPromise;
    }

    const openPromise = new Promise((resolve, reject) => {
      const request = window.indexedDB.open(this.#dbName, this.#dbVersion);

      request.onupgradeneeded = (event) => this.#onUpgrade(event);

      request.onblocked = () => {
        // Another tab still holds an older connection and isn't closing it.
        // Don't hang forever — reject so the app can render without the DB.
        console.error("IndexedDB open blocked by another tab");
        reject(new Error("IndexedDB open blocked by another tab"));
      };

      request.onsuccess = () => {
        const db = request.result;

        db.onversionchange = () => {
          db.close();
          this.#dbPromise = null;
        };

        resolve(db);
      };

      request.onerror = () => {
        reject(request.error || new Error("Failed to open IndexedDB"));
      };
    });

    // Safety net: never let an open hang block the app indefinitely.
    this.#dbPromise = Promise.race([
      openPromise,
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error("IndexedDB open timed out")), 15000);
      }),
    ]).catch((err) => {
      // Reset so a later call can retry instead of caching the rejection.
      this.#dbPromise = null;
      throw err;
    });

    return this.#dbPromise;
  }

  /**
   * Helper function to perform a transaction with the given mode and operation.
   *
   * @param objectStoreName - Name of object store
   * @param {string} mode - The transaction mode ("readonly" or "readwrite").
   * @param {function} operation - The operation to perform, which receives the object store and resolve/reject callbacks.
   * @returns {Promise} - A promise that resolves with the result of the operation or rejects with an error.
   */
  async tx(objectStoreName, mode, operation) {
    const db = await this.connect();

    if (!db) {
      throw new Error("IndexedDB is not available");
    }

    if (mode !== "readonly" && mode !== "readwrite") {
      throw new Error(`Invalid transaction mode "${mode}"`);
    }

    if (typeof operation !== "function") {
      throw new Error(`Operation must be a function`);
    }

    // utility function for getting a value by key, to simplify common patterns in operations
    const getByKey = (store) => (key) =>
      new Promise((resolve, reject) => {
        const req = store.get(key);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () =>
          reject(
            req.error || new Error(`Failed to get key ${key} from IndexedDB`)
          );
      });

    return new Promise((resolve, reject) => {
      const tx = db.transaction(objectStoreName, mode);
      const store = tx.objectStore(objectStoreName);

      operation({
        // store with all methods (get, put, add, delete, etc.)
        store,

        // helper function to get a value by key,
        // to simplify common patterns in operations
        getByKey: getByKey(store),

        // resolve and reject callbacks for the operation
        // to call when done (optional) or on error
        resolve,
        reject,
      });

      tx.onerror = () =>
        reject(
          tx.error || new Error(`IndexedDB transaction (${operation}) failed`)
        );
      tx.onabort = () =>
        reject(
          tx.error || new Error(`IndexedDB transaction (${operation}) aborted`)
        );
    });
  }

  /**
   * Performs a write transaction on the object store.
   *
   * Function is called with 3 arguments: the object store, a resolve callback, and a reject callback.
   * The function should perform the desired operations on the store and call resolve(result) when done,
   * or reject(error) if an error occurs.
   *
   * See https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore for available operations on the store.
   *
   * @param {string} objectStoreName - The name of the object store to perform the transaction on.
   * @param {function} fn - The operation to perform, which receives the object store and resolve/reject callbacks.
   * @returns {Promise} - A promise that resolves with the result of the operation or rejects with an error.
   */
  async txReadwrite(objectStoreName, fn) {
    return this.tx(objectStoreName, "readwrite", fn);
  }

  /**
   * Performs a read transaction on the object store.
   *
   * Function is called with 3 arguments: the object store, a resolve callback, and a reject callback.
   * The function should perform the desired operations on the store and call resolve(result) when done,
   * or reject(error) if an error occurs.
   *
   * See https://developer.mozilla.org/en-US/docs/Web/API/IDBObjectStore for available operations on the store.
   *
   * @param {string} objectStoreName - The name of the object store to perform the transaction on.
   * @param {function} fn - The operation to perform, which receives the object store and resolve/reject callbacks.
   * @returns {Promise} - A promise that resolves with the result of the operation or rejects with an error.
   */
  async txReadonly(objectStoreName, fn) {
    return this.tx(objectStoreName, "readonly", fn);
  }

  /**
   * Fetches a paginated list of items from the object store,
   * applying optional filter and sort functions.
   *
   * We're doing a brute-force getAll and then filtering/sorting/paginating
   * in-memory for simplicity. This is not optimal for large datasets,
   * but IndexedDB's querying capabilities are limited and this approach
   * is more flexible for complex filters/sorts.
   *
   * @param {function} filter - A function that takes an item and returns true if it should be included in the results.
   * @param {function} sort - A function that defines the sort order of the results (e.g., (a, b) => a.timestamp - b.timestamp).
   * @returns {Promise<Array>} A promise that resolves with an array of items from the object store that match the filter and sorted according to the sort function.
   */
  async query(
    objectStoreName,
    { filter = null, sort = null, pageNo = 0, pageSize = 20 } = {}
  ) {
    if (filter && typeof filter !== "function") {
      throw new Error("Filter, when provided, must be a function");
    }

    if (sort && typeof sort !== "function") {
      throw new Error("Sort, when provided, must be a function");
    }

    const db = await this.connect();

    if (!db) return []; // IndexedDB not available, treat as empty

    const tx = db.transaction(objectStoreName, "readonly");
    const store = tx.objectStore(objectStoreName);

    const request = store.getAll();

    return new Promise((resolve, reject) => {
      request.onsuccess = (event) => {
        let items = event.target.result;

        if (filter) {
          items = items.filter(filter);
        }

        if (sort && typeof sort === "function") {
          items.sort(sort);
        }

        // apply pagination
        const startIndex = pageNo * pageSize;

        const content =
          pageSize < 1 ? items : items.slice(startIndex, startIndex + pageSize);

        // structure here mimics the typical API response for
        // paginated data, with content and totalElements
        resolve({
          content,
          totalElements: items.length,
          numberOfElements: content.length,
          size: content.length,
          first: pageNo === 0,
          last: pageSize < 1 ? true : startIndex + pageSize >= items.length,
          totalPages: pageSize < 1 ? 1 : Math.ceil(items.length / pageSize),
          empty: items.length === 0,
          number: pageNo,

          pageable: {
            pageNumber: pageNo,
            pageSize,
            paged: items.length > content.length,
          },
        });
      };

      request.onerror = () =>
        reject(
          request.error || new Error("Failed to get items from IndexedDB store")
        );
    });
  }

  async all(objectStoreName) {
    const db = await this.connect();

    if (!db) return []; // IndexedDB not available, treat as empty

    const tx = db.transaction(objectStoreName, "readonly");
    const store = tx.objectStore(objectStoreName);

    const request = store.getAll();

    return new Promise((resolve, reject) => {
      request.onsuccess = (event) => {
        resolve(event.target.result);
      };

      request.onerror = () =>
        reject(
          request.error || new Error("Failed to get items from IndexedDB store")
        );
    });
  }

  /**
   * Counts the number of items in the object store, optionally applying a filter.
   *
   * @param {string} objectStoreName - The name of the object store to count items in.
   * @param {function} filter - An optional filter function to apply to the items.
   * @returns {Promise<number>} A promise that resolves with the count of items.
   */
  async count(objectStoreName, filter = null) {
    const db = await this.connect();

    if (!db) return 0; // IndexedDB not available, treat as empty

    const tx = db.transaction(objectStoreName, "readonly");
    const store = tx.objectStore(objectStoreName);

    const request = store.getAll();

    return new Promise((resolve, reject) => {
      request.onsuccess = (event) => {
        let items = event.target.result;

        if (filter && typeof filter === "function") {
          items = items.filter(filter);
        }

        resolve(items.length);
      };

      request.onerror = () =>
        reject(
          request.error || new Error("Failed to get items from IndexedDB store")
        );
    });
  }

  /**
   * Reads an item from the object store by its key.
   *
   * @param {string} objectStoreName - The name of the object store to read from.
   * @param {*} key - The key of the item to read.
   * @returns {Promise<any>} A promise that resolves with the item, or rejects if not found or an error occurs.
   */
  async read(objectStoreName, key) {
    return this.txReadonly(objectStoreName, ({ getByKey, resolve, reject }) => {
      getByKey(key)
        .then(resolve)
        .catch((error) =>
          reject(
            error || new Error(`Failed to read key ${key} from IndexedDB store`)
          )
        );
    });
  }

  /**
   * Stores one or more items in the object store.
   *
   * Each item should be an object that includes the key
   * defined by the keyPath (default is "id").
   *
   * If an item with the same key already exists, it will be updated.
   *
   * @param {string} objectStoreName - The name of the object store to insert items into.
   * @param {...object} items - One or more items to store in the object store.
   * @returns {Promise<object|object[]>} A promise that resolves with the inserted item, or an array of inserted items when multiple items are provided.
   */
  async insert(objectStoreName, ...items) {
    const $timestamp = new Date().toISOString();
    return this.txReadwrite(objectStoreName, ({ store, resolve }) => {
      const insertedItems = items.map((item) => ({ ...item, $timestamp }));

      insertedItems.forEach((item) => store.put(item));
      resolve(insertedItems.length === 1 ? insertedItems[0] : insertedItems);
    });
  }

  /**
   * Removes one or more items from the object store by their keys.
   *
   * @param {string} objectStoreName - The name of the object store to delete items from.
   * @param  {...any} keys - One or more keys of the items to be removed from the object store.
   * @returns Promise
   */
  async delete(objectStoreName, ...keys) {
    return this.txReadwrite(objectStoreName, ({ store, resolve }) => {
      keys.forEach((key) => store.delete(key));
      resolve();
    });
  }

  /**
   * Removes all records from a specific object store without dropping the store itself.
   *
   * @param {string} objectStoreName - The name of the object store to clear.
   * @returns Promise
   */
  async clearStore(objectStoreName) {
    return this.txReadwrite(objectStoreName, ({ store, resolve }) => {
      store.clear();
      resolve();
    });
  }

  /**
   * Removes all object stores from the database.
   *
   * Use with caution, as this will delete all cached data.
   *
   * @returns
   */
  async clear() {
    // Close the cached connection first so it doesn't block its own delete.
    const cached = await this.#dbPromise?.catch(() => null);
    if (cached) {
      cached.close();
      this.#dbPromise = null;
    }

    return new Promise((resolve, reject) => {
      const request = window.indexedDB.deleteDatabase(this.#dbName);
      request.onblocked = () =>
        reject(
          new Error(
            `Failed to clear IndexedDB: database deletion is blocked by open connections`
          )
        );
      request.onerror = () =>
        reject(
          request.error || new Error(`Failed to clear IndexedDB: unknown error`)
        );
      request.onsuccess = () => resolve();
    });
  }

  async getDirtyEntries() {
    const db = await this.connect();
    const dirtyEntries = await Promise.all(
      Array.from(db.objectStoreNames).map(async (storeName) => {
        const result = await this.query(storeName, {
          filter: (entry) => entry?.$dirty != null,
        });

        return (
          result?.content?.map((entry) => ({
            ident: storeName,
            data:
              entry?.$dirty === "UPLOAD"
                ? { ...entry, $dto: { ...entry.$dto, id: entry?.id } }
                : entry,
          })) || []
        );
      })
    );

    return dirtyEntries
      .flat()
      .sort(
        (a, b) => new Date(a.data.$timestamp) - new Date(b.data.$timestamp)
      );
  }
}
